import React, { useMemo } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { useDashboradDataQuery } from '../../../store/slices/apiSlice';
import AnimatedBloqcubeLogo from '../../../components/Logo/AnimatedBloqcubeLogo';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const QueryStatisticsBarChart = (props) => {
  const sponsorId = useSelector((state) => state?.userDetails?.loggedInUser.sponsorId);

  const { data: dashboardData, isLoading } = useDashboradDataQuery({
    data: { preference: props.preference, sponsorId: sponsorId, siteId: props.site, trialId: props.trial },
  });

  const labels = useMemo(() => {
    if (dashboardData) {
      return dashboardData[props?.preference]?.map((_site) => _site?.site?.orgname);
    } else {
      return [];
    }
  }, [dashboardData]);

  const openCounts = useMemo(() => {
    if (dashboardData) {
      return dashboardData[props?.preference]?.map((_site) => _site?.OPEN || 0);
    } else {
      return [];
    }
  }, [dashboardData]);

  const resolvedCounts = useMemo(() => {
    if (dashboardData) {
      return dashboardData[props?.preference]?.map((_site) => _site?.RESOLVED || 0);
    } else {
      return [];
    }
  }, [dashboardData]);

  const verifiedCounts = useMemo(() => {
    if (dashboardData) {
      return dashboardData[props?.preference]?.map((_site) => _site?.VERIFIED || 0);
    } else {
      return [];
    }
  }, [dashboardData]);

  const reopenedCounts = useMemo(() => {
    if (dashboardData) {
      return dashboardData[props?.preference]?.map((_site) => _site?.REOPENED || 0);
    } else {
      return [];
    }
  }, [dashboardData]);

  const options = {
    indexAxis: 'x', // Vertical bars
    elements: {
      bar: {
        borderWidth: 2,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
      datalabels: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          callback: function (value) {
            if (Number.isInteger(value) || value == 0) {
              return value;
            }
          },
        },
      },
    },
  };

  const data = {
    labels,
    datasets: [
      {
        label: 'Open',
        data: openCounts,
        borderColor: '#FF9800',
        backgroundColor: 'rgba(255, 152, 0, 0.7)',
      },
      {
        label: 'Resolved',
        data: resolvedCounts,
        borderColor: '#2196F3',
        backgroundColor: 'rgba(33, 150, 243, 0.7)',
      },
      {
        label: 'Verified',
        data: verifiedCounts,
        borderColor: '#4CAF50',
        backgroundColor: 'rgba(76, 175, 80, 0.7)',
      },
      {
        label: 'Reopened',
        data: reopenedCounts,
        borderColor: '#F44336',
        backgroundColor: 'rgba(244, 67, 54, 0.7)',
      },
    ],
  };

  return (
    <Box
      onMouseDown={(e) => e.stopPropagation()}
      sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', p: 1 }}
    >
      {isLoading && (
        <Box sx={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Box sx={{ height: 250, width: 250 }}>
            <AnimatedBloqcubeLogo />
          </Box>
        </Box>
      )}
      {dashboardData && <Bar options={options} data={data} />}
    </Box>
  );
};

export default QueryStatisticsBarChart;

